// Forum client entry (plain TS + three.js). The forum IS the main page.
//
// On load the party renders immediately and the camera idly orbits it; the
// avatar/candidate picker shows as a MODAL on top (party visible behind). Once
// you enter, it switches to the chase camera and play controls. The HUD's
// "🚪 Salir" reopens the picker modal.
//
// Controls (rotate + move, no WASD): joystick x=rotate y=forward; arrows ↑↓
// move ←→ rotate; scroll vertical=forward horizontal=rotate.
import * as THREE from "three";
import { buildForumScene } from "./scene/build.ts";
import { blackLine } from "./scene/materials.ts";
import { clampInsideHex, HEX_APOTHEM } from "./scene/hex.ts";
import { expertWalls, rosterWall } from "./scene/walls.ts";
import { PARTNER_PLACES } from "./scene/partners.ts";
import { displayName } from "../lib/candidates.ts";
import { type BadgeChoice, showBadgePicker } from "./ui/badge-picker.ts";
import { openChat } from "./ui/chat-panel.ts";
import { openTopic } from "./ui/topic-modal.ts";
import { openExperts } from "./ui/experts-modal.ts";
import { openPartner } from "./ui/partner-modal.ts";
import { createJoystick } from "./ui/joystick.ts";
import { type Controls, createControls } from "./ui/controls.ts";
import { openHelp } from "./ui/help.ts";
import { showWelcome } from "./ui/welcome.ts";
import { loadBoard } from "./board.ts";
import { loadSponsors } from "./sponsors.ts";
import { loadPartners } from "./partners.ts";
import { loadMusic } from "./music.ts";
import { createMusicPlayer } from "./ui/music-player.ts";

const root = document.getElementById("forum-root");
if (root) startForum(root);

async function startForum(mount: HTMLElement): Promise<void> {
  const [board, sponsors, partners, music] = await Promise.all([
    loadBoard(),
    loadSponsors(),
    loadPartners(),
    loadMusic(),
  ]);

  // Comic Neue isn't installed on most phones; wait for the web font before we
  // bake text into canvas textures (billboards, signs, plates) — otherwise they
  // render with a fallback face and never refresh.
  if (document.fonts?.load) {
    try {
      await Promise.all([
        document.fonts.load('400 40px "Comic Neue"'),
        document.fonts.load('700 40px "Comic Neue"'),
      ]);
    } catch { /* fall back to system comic / cursive */ }
  }

  const w0 = globalThis.innerWidth;
  const h0 = globalThis.innerHeight;

  const renderer = new THREE.WebGLRenderer({ antialias: true });
  renderer.setSize(w0, h0);
  renderer.setPixelRatio(Math.min(globalThis.devicePixelRatio, 2));
  renderer.setClearColor(0xffffff, 1);
  mount.appendChild(renderer.domElement);

  const camera = new THREE.PerspectiveCamera(60, w0 / h0, 0.1, 200);

  const lineMaterial = blackLine(w0, h0);
  const {
    scene,
    stations,
    crowd,
    partners: partnersRig,
    dispose: disposeScene,
  } = buildForumScene(
    lineMaterial,
    board,
    sponsors,
    partners,
  );

  // Invisible player (position + heading); no avatar body drawn.
  const player = new THREE.Group();
  player.position.set(0, 0, 5);
  scene.add(player);
  let heading = Math.PI; // facing the centre

  // ── Inputs (created once; joystick hidden during the intro) ───────────────
  const joystick = createJoystick();
  joystick.setVisible(false);
  const keys = new Set<string>();
  const onKeyDown = (e: KeyboardEvent) => keys.add(e.code);
  const onKeyUp = (e: KeyboardEvent) => keys.delete(e.code);
  globalThis.addEventListener("keydown", onKeyDown);
  globalThis.addEventListener("keyup", onKeyUp);

  let scrollFwd = 0;
  let scrollRot = 0;
  const onWheel = (e: WheelEvent) => {
    e.preventDefault();
    scrollFwd = THREE.MathUtils.clamp(scrollFwd - e.deltaY * 0.01, -2.5, 2.5);
    scrollRot = THREE.MathUtils.clamp(scrollRot + e.deltaX * 0.01, -2.5, 2.5);
  };
  renderer.domElement.addEventListener("wheel", onWheel, { passive: false });

  // Ambient soundtrack — persistent across intro/play. Mounted once; playback
  // starts on entry (a user gesture, so the browser allows it).
  const musicPlayer = createMusicPlayer(music);

  // ── Intro (orbit the party + modal) vs Play (chase cam + controls) ────────
  let mode: "intro" | "play" = "intro";
  let hud: Controls | null = null;
  let introAngle = 0;

  function enter(choice: BadgeChoice) {
    mode = "play";
    musicPlayer.start();
    joystick.setVisible(true);
    hud?.dispose();
    hud = createControls({
      kind: choice.kind,
      votingFor: choice.votingFor,
      onHelp: () => openHelp(board, partners),
      onExit: exitToIntro,
    });
    // Shown on every entry (not just first visit) — it's where we surface the
    // party explainer, the move/turn demo, and any new announcements.
    showWelcome(() => hud?.pulseHelp());
  }

  function exitToIntro() {
    mode = "intro";
    joystick.setVisible(false);
    hud?.dispose();
    hud = null;
    showBadgePicker(enter);
  }

  // ── Movement (play only): rotate + move forward, clamped to the room ──────
  const DEAD = 0.15;
  const MOVE_SPEED = 12;
  const ROT_SPEED = 2.4;
  const MARGIN = 1.0;
  const dz = (v: number) => (Math.abs(v) < DEAD ? 0 : v);

  const step = (dt: number) => {
    let rot = dz(joystick.x) + scrollRot;
    let fwd = -dz(joystick.y) + scrollFwd;
    if (keys.has("ArrowRight")) rot += 1;
    if (keys.has("ArrowLeft")) rot -= 1;
    if (keys.has("ArrowUp")) fwd += 1;
    if (keys.has("ArrowDown")) fwd -= 1;
    rot = THREE.MathUtils.clamp(rot, -1, 1);
    fwd = THREE.MathUtils.clamp(fwd, -1, 1);

    heading -= rot * ROT_SPEED * dt;
    if (fwd !== 0) {
      const c = clampInsideHex(
        player.position.x + Math.sin(heading) * fwd * MOVE_SPEED * dt,
        player.position.z + Math.cos(heading) * fwd * MOVE_SPEED * dt,
        MARGIN,
      );
      player.position.x = c.x;
      player.position.z = c.z;
    }
    scrollFwd *= 0.9;
    scrollRot *= 0.9;
    if (Math.abs(scrollFwd) < 0.001) scrollFwd = 0;
    if (Math.abs(scrollRot) < 0.001) scrollRot = 0;
  };

  // ── Camera: chase (play) or slow overview orbit (intro) ───────────────────
  const CAM_DIST = 5;
  const CAM_HEIGHT = 2.6;
  const EYE = 1.5;
  const INTRO_R = HEX_APOTHEM * 0.6;
  const INTRO_H = 9;
  const placeCamera = () => {
    if (mode === "play") {
      const fx = Math.sin(heading);
      const fz = Math.cos(heading);
      const c = clampInsideHex(
        player.position.x - fx * CAM_DIST,
        player.position.z - fz * CAM_DIST,
        0.5,
      );
      camera.position.set(c.x, CAM_HEIGHT, c.z);
      camera.lookAt(player.position.x, EYE, player.position.z);
    } else {
      camera.position.set(
        Math.cos(introAngle) * INTRO_R,
        INTRO_H,
        Math.sin(introAngle) * INTRO_R,
      );
      camera.lookAt(0, 2.5, 0);
    }
  };

  // Nearest interactable within range: a candidate booth (→ chat) or a topic
  // wall (→ ver cartelera). Returns what the HUD action button should show/do.
  const PROX = 12; // trigger the action button well before reaching the wall
  type Action = {
    key: string;
    label: string;
    onClick: () => void;
    hint: string;
  };
  const nearestAction = (): Action | null => {
    let best: Action | null = null;
    let bestDist = PROX;
    for (const [slug, st] of stations) {
      const d = Math.hypot(
        player.position.x - st.position.x,
        player.position.z - st.position.z,
      );
      if (d < bestDist) {
        bestDist = d;
        best = {
          key: `chat:${slug}`,
          label: `💬 Hablar con ${displayName(slug)}`,
          onClick: () => openChat(slug),
          hint: "haz clic para hablar",
        };
      }
    }
    for (const w of expertWalls) {
      const d = Math.hypot(
        player.position.x - w.slot.x,
        player.position.z - w.slot.z,
      );
      if (d < bestDist) {
        bestDist = d;
        const title = board.topics.find((t) => t.id === w.topicId)?.title ??
          w.topicId;
        best = {
          key: `topic:${w.topicId}`,
          label: `📋 Ver cartelera: ${title}`,
          onClick: () => openTopic(board, w.topicId),
          hint: "haz clic para leer",
        };
      }
    }
    const dr = Math.hypot(
      player.position.x - rosterWall.x,
      player.position.z - rosterWall.z,
    );
    if (dr < bestDist) {
      bestDist = dr;
      best = {
        key: "experts",
        label: "👥 Ver expertos consultados",
        onClick: () => openExperts(board),
        hint: "haz clic para leer",
      };
    }
    for (const place of PARTNER_PLACES) {
      const d = Math.hypot(
        player.position.x - place.x,
        player.position.z - place.z,
      );
      if (d < bestDist) {
        const p = partners.partners.find((x) => x.slug === place.slug);
        if (!p) continue;
        bestDist = d;
        best = {
          key: `partner:${place.slug}`,
          label: `🔗 Herramientas de ${p.name}`,
          onClick: () => openPartner(p),
          hint: "haz clic para ver",
        };
      }
    }
    return best;
  };

  // ── Loop ─────────────────────────────────────────────────────────────────
  const clock = new THREE.Clock();
  let raf = 0;
  const render = () => {
    raf = requestAnimationFrame(render);
    const dt = Math.min(clock.getDelta(), 0.05);
    if (mode === "play") {
      step(dt);
      if (hud) {
        const a = nearestAction();
        hud.setAction(
          a?.key ?? null,
          a?.label ?? "",
          a?.onClick ?? (() => {}),
          a?.hint,
        );
      }
    } else {
      introAngle += dt * 0.12; // slow overview orbit
    }
    crowd.update(dt, camera); // party always alive
    partnersRig.update(dt, camera); // partner-table logos bob + face camera
    placeCamera();
    renderer.render(scene, camera);
  };
  render();

  const onResize = () => {
    const w = globalThis.innerWidth;
    const h = globalThis.innerHeight;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h);
    lineMaterial.resolution.set(w, h);
  };
  globalThis.addEventListener("resize", onResize);

  globalThis.addEventListener("beforeunload", () => {
    cancelAnimationFrame(raf);
    globalThis.removeEventListener("resize", onResize);
    globalThis.removeEventListener("keydown", onKeyDown);
    globalThis.removeEventListener("keyup", onKeyUp);
    renderer.domElement.removeEventListener("wheel", onWheel);
    joystick.dispose();
    hud?.dispose();
    disposeScene();
    lineMaterial.dispose();
    renderer.dispose();
  });

  // Start in intro: party orbiting behind the picker modal.
  showBadgePicker(enter);
}
